import { useState, useEffect } from 'react';
import './App.css';
import HomePage from './HomePage';
import RegisterPage from './RegisterPage';
import LoginPage from './LoginPage';
import AboutUsPage from './AboutUsPage';
import EducationProgramPage from './EducationProgramPage';
import AdmissionsPage from './AdmissionsPage';
import StudentLifePage from './StudentLifePage';
import FacilitiesPage from './FacilitiesPage';
import NewsUpdatesPage from './NewsUpdatesPage';
import ParentsPage from './ParentsPage';
import ContactUsPage from './ContactUsPage';
import CareersPage from './CareersPage';
import SecondaryPage from './SecondaryPage';
import HouseDetailPage from './HouseDetailPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import SuperAdminDashboard from './SuperAdminDashboard';
import WebsiteAdminDashboard from './dashboards/WebsiteAdminDashboard';
import PrincipalDashboard from './dashboards/PrincipalDashboard';
import { API_BASE } from './config';

console.log('[App] API_BASE configured as:', API_BASE);

// Detect initial page from URL path
const getInitialPage = () => {
  const path = window.location.pathname;
  if (path === '/login' || path.startsWith('/login')) return 'login';
  if (path === '/register' || path.startsWith('/register')) return 'register';
  if (path.startsWith('/admin-dashboard')) return 'admin-dashboard';
  if (path.startsWith('/website-admin-cms')) return 'website-admin-cms';
  if (path.startsWith('/principal-dashboard') || path.startsWith('/approver')) return 'principal-dashboard';
  if (path.startsWith('/admissions-portal')) return 'admissions-portal';
  return 'home';
};

// Dashboard pages - these don't show the main Navbar
const DASHBOARD_PAGES = ['admin-dashboard', 'website-admin-cms', 'principal-dashboard', 'admissions-portal'];

function App() {
  const [language, setLanguage] = useState(() => {
    const saved = window.localStorage.getItem('phs-language');
    return saved === 'id' ? 'id' : 'en';
  });

  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [currentSubPage, setCurrentSubPage] = useState(null); // For hash-based pages
  const [headerMenu, setHeaderMenu] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);

  // Current logged-in user (from sessionStorage set by LoginPage)
  const [authUser, setAuthUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('auth_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Load menu from API (only for public pages)
  useEffect(() => {
    if (DASHBOARD_PAGES.includes(currentPage)) {
      setLoadingMenu(false);
      return;
    }
    const fetchMenu = async () => {
      try {
        setLoadingMenu(true);
        console.log('[App] Fetching menu from:', `${API_BASE}/menu`);
        const response = await fetch(`${API_BASE}/menu`, {
          signal: AbortSignal.timeout(5000),
        });
        console.log('[App] Menu response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('[App] Menu data received:', data);
          setHeaderMenu(data || []);
        } else {
          console.error('[App] Menu fetch failed:', response.status);
        }
      } catch (error) {
        console.error('[App] Menu fetch error:', error);
        // silent fail
      } finally {
        setLoadingMenu(false);
      }
    };
    fetchMenu();
  }, [currentPage]);

  const handleNavigate = (page) => {
    setCurrentPage(page);
    const paths = {
      'home': '/',
      'login': '/login',
      'register': '/register',
      'admin-dashboard': '/admin-dashboard',
      'website-admin-cms': '/website-admin-cms',
      'principal-dashboard': '/principal-dashboard',
      'admissions-portal': '/admissions-portal',
    };
    window.history.pushState({}, '', paths[page] || '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    sessionStorage.removeItem('auth_user');
    setAuthUser(null);
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    handleNavigate('home');
  };

  const handleGoToSlug = (slug) => {
    if (slug === 'register' || slug === 'admission') handleNavigate('register');
    else if (slug === 'login') handleNavigate('login');
    else if (slug === 'contact-us') setCurrentSubPage('contact-us');
    else {
      // For other pages, use state navigation
      setCurrentSubPage(slug);
    }
  };

  const handleSelectSlug = (item) => {
    // Navigate using state only (no hash)
    console.log('[App] handleSelectSlug called with:', item);
    setCurrentSubPage(item.slug);
  };

  const getSubmenuItemsForNavItem = (item) => item.children || [];

  const isDashboard = DASHBOARD_PAGES.includes(currentPage);

  return (
    <ErrorBoundary>
      <div className="app-shell">
        {!isDashboard && (
          <Navbar
            headerMenu={headerMenu}
            activeItemId={null}
            onSelectSlug={handleSelectSlug}
            goToSlug={handleGoToSlug}
            goToHomepage={() => {
              setCurrentSubPage(null);
              handleNavigate('home');
            }}
            getSubmenuItemsForNavItem={getSubmenuItemsForNavItem}
            goToSection={() => {}}
            loadingMenu={loadingMenu}
          />
        )}
        <main className={isDashboard ? '' : 'main-content main-content-home'}>
          {currentPage === 'home' && !currentSubPage && (
            <HomePage language={language} onLanguageChange={setLanguage} onNavigate={handleNavigate} />
          )}
          {currentPage === 'home' && currentSubPage === 'about-us' && <AboutUsPage />}
          {currentPage === 'home' && currentSubPage === 'education-program' && <EducationProgramPage onNavigate={(slug) => setCurrentSubPage(slug)} />}
          {currentPage === 'home' && currentSubPage === 'admissions' && <AdmissionsPage page={null} />}
          {currentPage === 'home' && currentSubPage === 'student-life' && <StudentLifePage page={null} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage?.startsWith('house-') && <HouseDetailPage houseName={currentSubPage} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage === 'facilities' && <FacilitiesPage page={null} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage === 'news-updates' && <NewsUpdatesPage page={null} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage === 'parents' && <ParentsPage page={null} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage === 'contact-us' && <ContactUsPage page={null} onNavigate={handleGoToSlug} />}
          {currentPage === 'home' && currentSubPage === 'careers' && <CareersPage page={null} />}
          {currentPage === 'home' && currentSubPage === 'secondary' && <SecondaryPage page={null} onNavigate={handleNavigate} />}
          {currentPage === 'register' && (
            <RegisterPage language={language} onLanguageChange={setLanguage} onNavigate={handleNavigate} />
          )}
          {currentPage === 'login' && (
            <LoginPage language={language} onLanguageChange={setLanguage} onNavigate={handleNavigate} />
          )}
          {currentPage === 'admin-dashboard' && (
            <SuperAdminDashboard
              user={authUser}
              onLogout={handleLogout}
              apiBase={API_BASE}
            />
          )}
          {currentPage === 'website-admin-cms' && (
            <WebsiteAdminDashboard
              user={authUser}
              onLogout={handleLogout}
              apiBase={API_BASE}
            />
          )}
          {currentPage === 'principal-dashboard' && (
            <PrincipalDashboard
              user={authUser}
              onLogout={handleLogout}
              apiBase={API_BASE}
            />
          )}
          {currentPage === 'admissions-portal' && (
            <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa' }}>
              <div style={{ background:'white', padding:'40px', borderRadius:'16px', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize:'48px' }}>🏫</div>
                <h2>Admissions Portal</h2>
                <p style={{ color:'#666' }}>Halaman ini sedang dalam pengembangan.</p>
                <button onClick={handleLogout} style={{ marginTop:'16px', padding:'10px 24px', background:'#4f46e5', color:'white', border:'none', borderRadius:'8px', cursor:'pointer' }}>
                  Logout
                </button>
              </div>
            </div>
          )}
        </main>
        {!isDashboard && <Footer onNavigate={handleGoToSlug} hideAccreditation={currentPage === 'login'} />}
      </div>
    </ErrorBoundary>
  );
}

export default App;
